/**
 * Customer Detail Page
 * Full customer profile with lifecycle management, onboarding, and communications
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  Avatar,
  LinearProgress,
  Tooltip,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  alpha,
  Skeleton,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  ArrowBack,
  Business,
  Person,
  Email,
  Phone,
  Language,
  Edit,
  Save,
  CheckCircle,
  Warning,
  Schedule,
  AttachMoney,
  Assignment,
  Message,
  Call,
  VideoCall,
  Note,
  Send,
  Refresh,
  OpenInNew,
  ContentCopy,
  MoreVert,
  Add,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';

// Types
interface Customer {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  company_name: string;
  company_website: string | null;
  industry: string | null;
  company_size: string | null;
  tier: string;
  status: string;
  add_ons: string[];
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  monthly_value: number;
  total_paid: number;
  retainer_paid: boolean;
  retainer_paid_at: string | null;
  registered_at: string | null;
  subscription_started_at: string | null;
  onboarding_started_at: string | null;
  onboarding_completed_at: string | null;
  last_activity_at: string | null;
  login_count: number;
  assigned_admin_id: string | null;
  assigned_admin_name: string | null;
  assigned_admin_email: string | null;
  internal_notes: string | null;
  created_at: string;
}

interface OnboardingTask {
  id: string;
  task_key: string;
  title: string;
  description: string | null;
  order_index: number;
  completed: boolean;
  completed_at: string | null;
  completed_by: string | null;
  due_date: string | null;
  required: boolean;
}

interface Activity {
  id: string;
  action: string;
  category: string;
  description: string | null;
  metadata: any;
  ip_address: string | null;
  created_at: string;
}

interface Communication {
  id: string;
  type: string;
  direction: string;
  subject: string | null;
  content: string;
  admin_name: string | null;
  scheduled_at: string | null;
  duration_minutes: number | null;
  created_at: string;
}

// Status configuration
const STATUS_STEPS = ['prospect', 'pending_subscription', 'pending_retainer', 'onboarding', 'active'];
const STATUS_LABELS: Record<string, string> = {
  prospect: 'Prospect',
  pending_subscription: 'Pending Subscription',
  pending_retainer: 'Pending Retainer',
  onboarding: 'Onboarding',
  active: 'Active',
  paused: 'Paused',
  churned: 'Churned',
  delinquent: 'Delinquent',
};

const TIER_LABELS: Record<string, string> = {
  starter: 'Core Site + Telephony',
  professional: 'Growth Stack',
  growth: 'Ops Suite',
  enterprise: 'Enterprise Transformation',
};

// Format helpers
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatDate = (date: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (date: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getRelativeTime = (date: string | null) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(date);
};

// Tab panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomerDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // State
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [onboardingTasks, setOnboardingTasks] = useState<OnboardingTask[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [stripeData, setStripeData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Communication dialog
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [newComm, setNewComm] = useState({
    type: 'note',
    direction: 'internal',
    subject: '',
    content: '',
  });

  // Fetch customer data
  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('redex_admin_token');

      const response = await fetch(`/api/admin/customers/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch customer');

      const data = await response.json();
      if (data.success) {
        setCustomer(data.data.customer);
        setOnboardingTasks(data.data.onboardingTasks);
        setActivities(data.data.activities);
        setCommunications(data.data.communications);
        setStripeData(data.data.stripeData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch customer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchCustomer();
  }, [id]);

  // Complete task
  const handleCompleteTask = async (taskId: string) => {
    try {
      const token = localStorage.getItem('redex_admin_token');
      const response = await fetch(`/api/admin/customers/${id}/tasks/${taskId}/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        fetchCustomer();
      }
    } catch (err) {
      console.error('Failed to complete task:', err);
    }
  };

  // Log communication
  const handleLogCommunication = async () => {
    try {
      const token = localStorage.getItem('redex_admin_token');
      const response = await fetch(`/api/admin/customers/${id}/communications`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newComm),
      });

      if (response.ok) {
        setCommDialogOpen(false);
        setNewComm({ type: 'note', direction: 'internal', subject: '', content: '' });
        fetchCustomer();
      }
    } catch (err) {
      console.error('Failed to log communication:', err);
    }
  };

  // Update customer status
  const handleStatusChange = async (newStatus: string) => {
    try {
      const token = localStorage.getItem('redex_admin_token');
      const response = await fetch(`/api/admin/customers/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        fetchCustomer();
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">Customer not found</Alert>
      </Container>
    );
  }

  const currentStatusIndex = STATUS_STEPS.indexOf(customer.status);
  const completedTasks = onboardingTasks.filter((t) => t.completed).length;
  const totalTasks = onboardingTasks.length;
  const onboardingProgress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/customers" color="inherit" underline="hover">
          Customers
        </Link>
        <Typography color="text.primary">{customer.company_name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontSize: '2rem',
                fontWeight: 700,
              }}
            >
              {customer.company_name.charAt(0)}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" fontWeight={700}>
                {customer.company_name}
              </Typography>
              <Chip
                label={STATUS_LABELS[customer.status]}
                color={customer.status === 'active' ? 'success' : customer.status === 'onboarding' ? 'primary' : 'default'}
              />
              <Chip label={TIER_LABELS[customer.tier] || customer.tier} variant="outlined" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, color: 'text.secondary' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Person fontSize="small" />
                <Typography variant="body2">
                  {customer.first_name} {customer.last_name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email fontSize="small" />
                <Typography variant="body2">{customer.email}</Typography>
              </Box>
              {customer.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone fontSize="small" />
                  <Typography variant="body2">{customer.phone}</Typography>
                </Box>
              )}
              {customer.company_website && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Language fontSize="small" />
                  <Link href={customer.company_website} target="_blank" rel="noopener">
                    {customer.company_website.replace(/^https?:\/\//, '')}
                  </Link>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {formatCurrency(customer.monthly_value)}/mo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                LTV: {formatCurrency(customer.total_paid)}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<Message />}
              onClick={() => setCommDialogOpen(true)}
            >
              Log Communication
            </Button>
          </Grid>
        </Grid>

        {/* Lifecycle Progress */}
        {currentStatusIndex >= 0 && currentStatusIndex < STATUS_STEPS.length && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Customer Lifecycle
            </Typography>
            <Stepper activeStep={currentStatusIndex} alternativeLabel>
              {STATUS_STEPS.map((step) => (
                <Step key={step}>
                  <StepLabel>{STATUS_LABELS[step]}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Overview" />
          <Tab
            label={
              <Badge badgeContent={totalTasks - completedTasks} color="warning">
                Onboarding
              </Badge>
            }
          />
          <Tab label="Activity" />
          <Tab label="Communications" />
          <Tab label="Billing" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          {/* Company Details */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Company Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Industry
                  </Typography>
                  <Typography variant="body1">{customer.industry || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Company Size
                  </Typography>
                  <Typography variant="body1">{customer.company_size || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Registered
                  </Typography>
                  <Typography variant="body1">{formatDate(customer.registered_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Last Activity
                  </Typography>
                  <Typography variant="body1">{getRelativeTime(customer.last_activity_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Login Count
                  </Typography>
                  <Typography variant="body1">{customer.login_count}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Assigned To
                  </Typography>
                  <Typography variant="body1">{customer.assigned_admin_name || 'Unassigned'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Quick Actions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" startIcon={<Email />}>
                    Send Email
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" startIcon={<Call />}>
                    Log Call
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" startIcon={<VideoCall />}>
                    Schedule Meeting
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" startIcon={<Note />}>
                    Add Note
                  </Button>
                </Grid>
                {customer.stripe_customer_id && (
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<OpenInNew />}
                      onClick={() => window.open(`https://dashboard.stripe.com/customers/${customer.stripe_customer_id}`, '_blank')}
                    >
                      View in Stripe
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Internal Notes */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Internal Notes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                fullWidth
                multiline
                rows={4}
                value={customer.internal_notes || ''}
                placeholder="Add internal notes about this customer..."
                variant="outlined"
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={<Save />}>
                  Save Notes
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Onboarding Tab */}
      <TabPanel value={activeTab} index={1}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Onboarding Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {completedTasks} of {totalTasks} tasks completed
              </Typography>
            </Box>
            <Box sx={{ width: 200 }}>
              <LinearProgress
                variant="determinate"
                value={onboardingProgress}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <List>
            {onboardingTasks.map((task) => (
              <ListItem
                key={task.id}
                sx={{
                  bgcolor: task.completed ? alpha(theme.palette.success.main, 0.05) : 'transparent',
                  borderRadius: 2,
                  mb: 1,
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={task.completed}
                    onChange={() => !task.completed && handleCompleteTask(task.id)}
                    disabled={task.completed}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle1"
                      sx={{ textDecoration: task.completed ? 'line-through' : 'none' }}
                    >
                      {task.title}
                      {task.required && (
                        <Chip label="Required" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  }
                  secondary={task.description}
                />
                <ListItemSecondaryAction>
                  {task.completed ? (
                    <Chip
                      label={`Completed ${formatDate(task.completed_at)}`}
                      color="success"
                      size="small"
                    />
                  ) : task.due_date ? (
                    <Chip
                      label={`Due ${formatDate(task.due_date)}`}
                      color="warning"
                      size="small"
                    />
                  ) : null}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {onboardingTasks.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No onboarding tasks yet. Tasks will be created when customer enters onboarding.
              </Typography>
            )}
          </List>
        </Paper>
      </TabPanel>

      {/* Activity Tab */}
      <TabPanel value={activeTab} index={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Activity Timeline
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {activities.length > 0 ? (
            <Timeline position="right">
              {activities.map((activity, index) => (
                <TimelineItem key={activity.id}>
                  <TimelineOppositeContent sx={{ flex: 0.2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(activity.created_at)}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot
                      color={
                        activity.category === 'payment' ? 'success' :
                        activity.category === 'auth' ? 'primary' :
                        activity.category === 'onboarding' ? 'info' :
                        'grey'
                      }
                    />
                    {index < activities.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="subtitle2">{activity.action.replace(/_/g, ' ')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {activity.description}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          ) : (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No activity recorded yet
            </Typography>
          )}
        </Paper>
      </TabPanel>

      {/* Communications Tab */}
      <TabPanel value={activeTab} index={3}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              Communications
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setCommDialogOpen(true)}>
              Log Communication
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <List>
            {communications.map((comm) => (
              <ListItem key={comm.id} sx={{ alignItems: 'flex-start', mb: 2 }}>
                <ListItemIcon>
                  {comm.type === 'email' ? <Email /> :
                   comm.type === 'call' ? <Call /> :
                   comm.type === 'meeting' ? <VideoCall /> :
                   <Note />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">
                        {comm.subject || `${comm.type.charAt(0).toUpperCase() + comm.type.slice(1)} - ${comm.direction}`}
                      </Typography>
                      <Chip label={comm.type} size="small" variant="outlined" />
                      <Chip label={comm.direction} size="small" />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {comm.content}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {comm.admin_name ? `By ${comm.admin_name} • ` : ''}{formatDateTime(comm.created_at)}
                        {comm.duration_minutes && ` • ${comm.duration_minutes} minutes`}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
            {communications.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No communications logged yet
              </Typography>
            )}
          </List>
        </Paper>
      </TabPanel>

      {/* Billing Tab */}
      <TabPanel value={activeTab} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Subscription Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={customer.subscription_status || 'No subscription'}
                      color={customer.subscription_status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Monthly Value
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(customer.monthly_value)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Subscription Started
                  </Typography>
                  <Typography variant="body1">{formatDate(customer.subscription_started_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Paid
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(customer.total_paid)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Retainer Payment
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={customer.retainer_paid ? 'Paid' : 'Pending'}
                      color={customer.retainer_paid ? 'success' : 'warning'}
                      size="small"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Paid Date
                  </Typography>
                  <Typography variant="body1">
                    {customer.retainer_paid_at ? formatDate(customer.retainer_paid_at) : '-'}
                  </Typography>
                </Grid>
              </Grid>
              {!customer.retainer_paid && customer.status === 'pending_retainer' && (
                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" fullWidth>
                    Send Retainer Invoice
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {stripeData && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Stripe Integration
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Customer ID
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontFamily="monospace">
                        {customer.stripe_customer_id}
                      </Typography>
                      <IconButton size="small" onClick={() => navigator.clipboard.writeText(customer.stripe_customer_id || '')}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Subscription Status
                    </Typography>
                    <Typography variant="body1">{stripeData.status}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Current Period End
                    </Typography>
                    <Typography variant="body1">
                      {stripeData.currentPeriodEnd
                        ? new Date(stripeData.currentPeriodEnd * 1000).toLocaleDateString()
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Communication Dialog */}
      <Dialog open={commDialogOpen} onClose={() => setCommDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Communication</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newComm.type}
                  label="Type"
                  onChange={(e) => setNewComm({ ...newComm, type: e.target.value })}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="call">Call</MenuItem>
                  <MenuItem value="meeting">Meeting</MenuItem>
                  <MenuItem value="chat">Chat</MenuItem>
                  <MenuItem value="note">Internal Note</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={newComm.direction}
                  label="Direction"
                  onChange={(e) => setNewComm({ ...newComm, direction: e.target.value })}
                >
                  <MenuItem value="inbound">Inbound</MenuItem>
                  <MenuItem value="outbound">Outbound</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={newComm.subject}
                onChange={(e) => setNewComm({ ...newComm, subject: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Content"
                value={newComm.content}
                onChange={(e) => setNewComm({ ...newComm, content: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLogCommunication}>
            Log Communication
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomerDetailPage;
